<template>
  <!-- Clicks stay here. The dialog is often rendered inside a card that navigates on click. -->
  <dialog ref="dialogRef" class="gcm-dialog" @close="emit('close')" @click.stop="handleBackdropClick">
    <div class="gcm-card" :class="{ wide }">
      <div class="gcm-title" :class="{ 'has-body': !!slots.default }">{{ title }}</div>
      <div v-if="slots.default" class="gcm-body">
        <slot />
      </div>
      <slot name="error" />
      <div class="gcm-actions">
        <button class="gcm-keep" @click="emit('keep')">{{ keepLabel }}</button>
        <button
          v-if="destructiveLabel"
          class="gcm-destructive"
          :class="{ neutral: neutralConfirm }"
          :disabled="destructiveDisabled"
          @click="emit('confirm')"
        >
          {{ destructiveLabel }}
        </button>
      </div>
      <div class="gcm-close" @click="emit('close')">
        <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
      </div>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { onBeforeUnmount, onMounted, ref, useSlots } from 'vue';

defineProps<{
  title: string;
  keepLabel: string;
  destructiveLabel?: string;
  destructiveDisabled?: boolean;
  neutralConfirm?: boolean;
  wide?: boolean;
}>();

const emit = defineEmits<{ close: []; keep: []; confirm: [] }>();
const slots = useSlots();

const dialogRef = ref<HTMLDialogElement>();

onMounted(() => {
  dialogRef.value?.showModal();
});

onBeforeUnmount(() => {
  if (dialogRef.value?.open) dialogRef.value.close();
});

const handleBackdropClick = (e: MouseEvent) => {
  if (e.target === dialogRef.value) emit('close');
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.gcm-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  // The dialog fills a viewport that runs under the notch and the home
  // indicator, so the card is centred inside the safe area instead.
  padding: var(--sai-top) 0 var(--sai-bottom);
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.gcm-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  max-width: 300px;
  width: 100%;

  &.wide {
    max-width: 360px;
  }
}

.gcm-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-white);
  margin-bottom: 3rem;

  &.has-body {
    margin-bottom: var(--spacing-l);
  }
}

.gcm-body {
  width: 100%;
  margin-bottom: var(--spacing-xl);
}

.gcm-actions {
  margin-top: auto;
  width: 100%;
}

.gcm-keep {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  background: var(--color-linear-gradient);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 200ms ease;

  @include hover {
    opacity: 0.9;
  }
}

.gcm-destructive {
  margin-top: var(--spacing-md);
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  background: transparent;
  color: #e74c3c;
  font-size: 15px;
  font-weight: 600;
  width: 100%;
  cursor: pointer;
  transition: all 200ms ease;

  @include hover {
    background: rgba(231, 76, 60, 0.1);
  }

  &.neutral {
    color: var(--color-white-70);

    @include hover {
      background: var(--color-white-5);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.gcm-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    color: var(--color-white);
    border-color: var(--color-white-20);
  }

  @include press($transform: translateX(-50%));
}
</style>
